import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import PropertyEditor from "../StyleClassEditor/components/PropertyEditor"
import { useCSSClassManager } from "../hooks/useCSSClassManager"
import { useStyleGeneration } from "../StyleClassEditor/hooks/useStyleGeneration"
import { getDefaultProperties } from "../StyleClassEditor/data/property-definitions"
import PubSub from "pubsub-js"

const ClassPropertyDrawer = ({
  isOpen,
  onClose,
  selectedClass,
  htmlContent = ""
}) => {
  const [currentClass, setCurrentClass] = useState(null)
  const [originalNonClassCSS, setOriginalNonClassCSS] = useState("")

  // Use centralized CSS class manager
  const cssManager = useCSSClassManager()


  const {
    parseClassesFromHTML,
    parseOriginalClassesFromHTML,
    extractClassProperties,
    extractStyleContent,
    generateUpdatedHTML
  } = useStyleGeneration()

  // Initialize classes data when dialog opens - same pattern as StyleClassEditor
  useEffect(() => {
    if (htmlContent && isOpen) {
      // Set global flag to prevent any UI interference
      window._isPropertyEditorActive = true

      // Notify that property editor is now active
      PubSub.publish("property-editor:state", { state: "opened" })

      // Note: HtmlEditor will get the document from the current DOM when needed for surgical updates

      // Get current content from content manager - NO FALLBACKS
      const getCurrentContent = () => {
        return new Promise((resolve, reject) => {
          const token = PubSub.subscribe("html-editor:current-content-response", (msg, data) => {
            PubSub.unsubscribe(token)
            if (data.htmlContent) {
              resolve(data.htmlContent)
            } else {
              reject(new Error("No HTML content received from content manager"))
            }
          })

          // Request current content from content manager
          PubSub.publish("html-editor:get-current-content", {
            requestId: Date.now()
          })

          // Timeout without fallback - we MUST get content from content manager
          setTimeout(() => {
            PubSub.unsubscribe(token)
            reject(new Error("Timeout waiting for HTML content from content manager"))
          }, 1000)
        })
      }

      getCurrentContent()
        .then((freshContent) => {
        // Use the same approach as horizontal options menu - only process the selected class if it's original
        if (!selectedClass?.name) {
          return
        }

        // Get only the original classes that were defined in the source CSS
        const originalClassesInHTML = parseOriginalClassesFromHTML(freshContent)

        // Only proceed if the selected class was in the original source
        if (!originalClassesInHTML.includes(selectedClass.name)) {
          cssManager.initialize([])
          return
        }

        // Extract only the selected class properties using the same method as horizontal options menu
        const extractedProps = extractClassProperties(freshContent, selectedClass.name)

        // Create class object for just the selected class
        const classObject = {
          id: selectedClass.name,
          name: selectedClass.name,
          type: "CSS Class",
          properties: {
            ...getDefaultProperties(),
            ...extractedProps
          },
          isOriginal: true // Mark as original source class
        }

        // If the manager is not initialized, or our class doesn't exist, add it
        // But don't reinitialize the entire manager to avoid conflicts with StyleClassEditor
        if (!cssManager.isInitialized || !cssManager.getClass(classObject.name)) {
          cssManager.upsertClass(classObject.name, classObject.properties, true)
        }

        // Extract non-class CSS for later reconstruction (only exclude our selected class)
        const { nonClassCSS } = extractStyleContent(freshContent, [selectedClass.name])
        setOriginalNonClassCSS(nonClassCSS)

        // Set current class to the version from the manager (which may have been updated)
        setCurrentClass(cssManager.getClass(classObject.name) || classObject)
        })
        .catch((error) => {
          // Don't reinitialize manager on error - just clear local state
          setOriginalNonClassCSS("")
          setCurrentClass(null)
        })
    } else if (!htmlContent) {
      // Don't reinitialize manager - just clear local state
      setOriginalNonClassCSS("")
    }
  }, [htmlContent, isOpen, selectedClass, parseClassesFromHTML, parseOriginalClassesFromHTML, extractClassProperties, extractStyleContent, cssManager])


  // Handle updating a class with live preview
  const handleClassUpdate = (property, value) => {
    if (!currentClass?.name) {
      return
    }

    try {
      // Ensure the class exists in the manager before updating
      if (!cssManager.getClass(currentClass.name)) {
        cssManager.upsertClass(currentClass.name, currentClass.properties, true)
      }

      // Use centralized manager - it handles surgical updates automatically
      const updatedClass = cssManager.updateProperty(currentClass.name, property, value)

      // Update current class state with the result from centralized manager
      setCurrentClass(updatedClass)
    } catch (error) {
      console.error("Error updating class property:", error)
    }
  }

  const handleClose = () => {
    // Clear global flag immediately
    window._isPropertyEditorActive = false

    // Notify that property editor is now closed
    PubSub.publish("property-editor:state", { state: "closed" })

    setCurrentClass(null)
    setOriginalNonClassCSS("")
    onClose?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant="admin_drawer_right"
      closeOnClickOutside={true}
      passInteractionEvents={true}
      title={`Edit Class: .${currentClass?.name || ''}`}
    >
      {currentClass ? (
        <div className="flex-1 overflow-hidden">
          {/* Class Info Header */}
          <div className="p-4 border-b border-admin_bordered bg-admin_paper">
            <div className="flex items-center gap-2">
              <span className="text-admin_text_secondary">.</span>
              <span className="font-mono text-lg bg-admin_neutral_1 px-3 py-1 rounded">
                {currentClass.name}
              </span>
            </div>
            <Typography variant="admin_body_small" className="text-admin_text_secondary mt-2">
              Edit CSS properties for this class. Changes apply immediately.
            </Typography>
          </div>

          {/* Property Editors */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
            <PropertyEditor
              isOpen={true}
              onClose={() => {}} // Don't close from inner component
              currentClass={currentClass}
              onClassUpdate={handleClassUpdate}
              isCreatingNew={false}
              hideHeader={true} // Hide the class name editor since we show it above
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <Typography variant="admin_body1" className="text-admin_text_secondary">
            No class selected
          </Typography>
        </div>
      )}
    </Modal>
  )
}

export default ClassPropertyDrawer